import {zodResolver} from '@hookform/resolvers/zod';
import {NumberInput} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import React, {useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';
import type {OshaEmploymentInformationResponse} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {Spinner} from 'src/design/atoms/Spinner';
import {ViewField} from 'src/design/atoms/ViewField';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {
  useGetOshaEmploymentInformationQuery,
  useUpdateOshaEmploymentInformationMutation,
} from 'src/redux/queries/osha';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

const employmentSchema = z.object({
  annualAverageEmployeeCount: z
    .number({invalid_type_error: 'Enter a number'})
    .int('Enter a whole number')
    .min(0, 'Must be zero or greater'),
  totalHoursWorked: z
    .number({invalid_type_error: 'Enter a number'})
    .int('Enter a whole number')
    .min(0, 'Must be zero or greater'),
});

type EmploymentFormValues = z.infer<typeof employmentSchema>;

const toFormValues = (
  data: OshaEmploymentInformationResponse | undefined
): EmploymentFormValues => ({
  annualAverageEmployeeCount: data?.annualAverageEmployeeCount ?? 0,
  totalHoursWorked: data?.totalHoursWorked ?? 0,
});

export interface OshaEmploymentCardProps {
  projectId: number;
  year: number;
  canUpdate: boolean;
}

export const OshaEmploymentCard: React.FC<OshaEmploymentCardProps> = ({
  projectId,
  year,
  canUpdate,
}) => {
  const {setAlert} = useAlert();
  const [isEditing, setIsEditing] = useState(false);

  const {
    data: employment,
    isLoading,
    isError,
  } = useGetOshaEmploymentInformationQuery({projectId, year});
  const [updateEmployment, {isLoading: isSaving}] =
    useUpdateOshaEmploymentInformationMutation();

  const {control, handleSubmit, reset} = useForm<EmploymentFormValues>({
    defaultValues: toFormValues(undefined),
    resolver: zodResolver(employmentSchema),
  });

  useEffect(() => {
    if (employment) reset(toFormValues(employment));
  }, [employment, reset]);

  // Leaving edit mode when the year changes avoids saving to the wrong year.
  useEffect(() => {
    setIsEditing(false);
  }, [year]);

  if (isLoading) {
    return (
      <SectionCard title="Employment Information">
        <Box p="md">
          <Spinner loadingText="Loading employment information..." />
        </Box>
      </SectionCard>
    );
  }

  if (isError || !employment) {
    return (
      <SectionCard title="Employment Information">
        <Box p="md">
          <ErrorAlert message="Employment information could not be loaded. Please try again." />
        </Box>
      </SectionCard>
    );
  }

  const handleEdit = () => {
    reset(toFormValues(employment));
    setIsEditing(true);
  };

  const handleCancel = () => {
    reset(toFormValues(employment));
    setIsEditing(false);
  };

  const handleSave = handleSubmit(async values => {
    try {
      await updateEmployment({
        projectId,
        year,
        annualAverageEmployeeCount: values.annualAverageEmployeeCount,
        totalHoursWorked: values.totalHoursWorked,
      }).unwrap();
      setAlert('Employment information saved', 'success');
      setIsEditing(false);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  });

  const editAction =
    !isEditing && canUpdate ? (
      <Button size="sm" variant="subtle" onClick={handleEdit}>
        Edit
      </Button>
    ) : undefined;

  return (
    <SectionCard
      title={`Employment Information (${year})`}
      rightSection={editAction}>
      <Box p="md">
        <Stack gap="md">
          {isEditing ? (
            <>
              <Controller
                name="annualAverageEmployeeCount"
                control={control}
                render={({field, fieldState}) => (
                  <NumberInput
                    label="Annual average number of employees"
                    min={0}
                    allowDecimal={false}
                    allowNegative={false}
                    thousandSeparator=","
                    value={field.value}
                    onChange={value =>
                      field.onChange(value === '' ? '' : Number(value))
                    }
                    onBlur={field.onBlur}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="totalHoursWorked"
                control={control}
                render={({field, fieldState}) => (
                  <NumberInput
                    label="Total hours worked by all employees"
                    min={0}
                    allowDecimal={false}
                    allowNegative={false}
                    thousandSeparator=","
                    value={field.value}
                    onChange={value =>
                      field.onChange(value === '' ? '' : Number(value))
                    }
                    onBlur={field.onBlur}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Group>
                <Button variant="subtle" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button onClick={handleSave} loading={isSaving}>
                  Save
                </Button>
              </Group>
            </>
          ) : (
            <Grid gutter="md">
              <Grid.Col span={{base: 12, sm: 6}}>
                <ViewField
                  label="Annual average number of employees"
                  value={employment.annualAverageEmployeeCount.toLocaleString()}
                />
              </Grid.Col>
              <Grid.Col span={{base: 12, sm: 6}}>
                <ViewField
                  label="Total hours worked by all employees"
                  value={employment.totalHoursWorked.toLocaleString()}
                />
              </Grid.Col>
            </Grid>
          )}
        </Stack>
      </Box>
    </SectionCard>
  );
};
